<script setup lang="ts">
import { computed, onUnmounted, ref, toRefs, watch } from 'vue';
import useLocale from '../../composables/useLocale';
import { storeToRefs } from 'pinia';
import useUiStore from '../../stores/ui';
import useRenditionStore from '../../stores/rendition';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faBackwardStep, faPlay, faForwardStep, faLocationCrosshairs } from '@fortawesome/pro-solid-svg-icons';

library.add([faBackwardStep, faPlay, faForwardStep, faLocationCrosshairs]);

type Emits = {
  (e: 'goToStart'): void;
  (e: 'goToPlayback'): void;
  (e: 'update:modelValue', value: boolean): void;
  (e: 'clearLastmark'): void;
};
const emit = defineEmits<Emits>();

type Props = {
  isAtEnd?: boolean;
  showGoToPlayback?: boolean;
  isFromLastmark?: boolean;
};
const props = defineProps<Props>();
const { isAtEnd, showGoToPlayback, isFromLastmark } = toRefs(props);

const { isGoToStartVisible, isControlsVisible, bottomBarHeight } = storeToRefs(useUiStore());
const { toggleVisibleGoToStart } = useUiStore();

const overlayBottom = computed(() => (isControlsVisible.value ? `${bottomBarHeight.value + 56}px` : '8px'));
const { isRTL } = storeToRefs(useRenditionStore());
const { t } = useLocale();

const skipIcon = computed(() => (isRTL.value ? 'fas fa-forward-step' : 'fas fa-backward-step'));

let fadeTimeout: ReturnType<typeof setTimeout>;

// Decided when the overlay opens: the lastmark flag is reset by the first logged navigation,
// which happens while the overlay is still showing.
const showStartOver = ref(false);

watch(
  isGoToStartVisible,
  (newVal, oldVal) => {
    clearTimeout(fadeTimeout);

    if (newVal) {
      showStartOver.value = !!(isAtEnd?.value || isFromLastmark?.value);
    }

    // When dialog hides (transition from true to false), clear the lastmark flag
    if (oldVal === true && newVal === false) {
      emit('clearLastmark');
    }
    // Only auto-hide if we're NOT showing "return to playback" Or "at end"
    // The return button should stay visible until user takes action
    if (newVal && !isAtEnd?.value && !showGoToPlayback?.value) {
      fadeTimeout = setTimeout(() => {
        toggleVisibleGoToStart(false);
      }, 5000);
    }
  },
  { immediate: true },
);

const goToStart = () => {
  emit('goToStart');
  toggleVisibleGoToStart(false);
};

const goToPlayback = () => {
  emit('goToPlayback');
  toggleVisibleGoToStart(false);
};

onUnmounted(() => clearTimeout(fadeTimeout));
</script>

<template>
  <v-fade-transition>
    <div
      v-if="isGoToStartVisible"
      class="go-to-start-overlay position-fixed d-flex justify-center align-center bg-transparent ma-2"
      data-testid="bp-go-to-start-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="start-title"
    >
      <div class="overlay-inner d-flex align-center justify-center">
        <v-sheet class="rounded-pill px-4 d-inline-flex align-center justify-center ga-3" height="48" tabindex="0">
          <span id="start-title" class="d-sr-only">
            {{ t('book_player.label_continue_reading') }}
          </span>

          <v-btn
            v-if="showStartOver && !showGoToPlayback"
            class="text-none px-1"
            color="surface"
            variant="text"
            :ripple="false"
            data-testid="bp-start-over-btn"
            :aria-label="t('book_player.button_start_over')"
            @click.stop="goToStart"
          >
            <FontAwesomeIcon size="lg" :icon="skipIcon" class="mr-2" />
            {{ t('book_player.button_start_over') }}
          </v-btn>

          <v-btn
            v-if="showGoToPlayback"
            class="text-none px-1"
            color="surface"
            variant="text"
            :ripple="false"
            data-testid="bp-go-to-playback-btn"
            :aria-label="t('book_player.button_return_to_narration')"
            @click.stop="goToPlayback"
          >
            <FontAwesomeIcon size="lg" icon="fas fa-location-crosshairs" class="mr-2" />
            {{ t('book_player.button_go_to_playback') }}
          </v-btn>

          <v-btn
            v-if="!isAtEnd && !showGoToPlayback"
            class="text-none px-1"
            color="surface"
            variant="text"
            :ripple="false"
            :aria-label="
              showGoToPlayback ? t('book_player.button_continue_browsing') : t('book_player.button_continue')
            "
            data-testid="bp-continue-btn"
            @click.stop="toggleVisibleGoToStart(false)"
          >
            {{ showGoToPlayback ? t('book_player.button_continue_browsing') : t('book_player.button_continue') }}
            <FontAwesomeIcon size="lg" icon="fas fa-play" class="ml-2" />
          </v-btn>
        </v-sheet>
      </div>
    </div>
  </v-fade-transition>
</template>

<style scoped lang="scss">
.go-to-start-overlay {
  bottom: v-bind(overlayBottom);
  left: 0;
  right: 0;
  z-index: 10;
  pointer-events: none;
  transition: bottom 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
  padding-top: env(safe-area-inset-top, 0px);
  padding-bottom: env(safe-area-inset-bottom, 0px);

  .overlay-inner {
    pointer-events: auto;
  }

  .v-sheet {
    background-color: rgb(var(--v-theme-inverse-surface), 0.9);
    border: 1px solid rgb(var(--v-theme-outline-variant));
    outline: solid 1px rgb(var(--v-theme-outline-variant)) !important;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2); // Added a shadow for depth
    pointer-events: auto;
    max-width: 100%;
  }
}
</style>
